import Seo from "@/components/Seo";
import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Check } from "lucide-react";
import { Reveal, MaskedLines } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import { SERVICES } from "@/lib/data";

function ServiceRow({ s, index }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-10%", "10%"]);
  const flip = index % 2 === 1;

  return (
    <div ref={ref} className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center" data-testid={`service-${s.num}`}>
      <div className={`lg:col-span-6 ${flip ? "lg:order-2" : ""}`}>
        <div className="overflow-hidden aspect-[4/3]">
          <motion.img style={{ y, scale: 1.15 }} src={s.image} alt={s.title} className="w-full h-full object-cover" />
        </div>
      </div>
      <div className={`lg:col-span-5 ${flip ? "lg:order-1 lg:col-start-1" : "lg:col-start-8"}`}>
        <span className="font-display text-gold text-7xl md:text-8xl leading-none">{s.num}</span>
        <h2 className="mt-4 font-display uppercase text-4xl md:text-5xl leading-[0.95] tracking-tight">
          <MaskedLines lines={[s.title]} />
        </h2>
        <Reveal delay={0.15}>
          <p className="mt-6 text-white/70 leading-relaxed">{s.body}</p>
          <ul className="mt-8 space-y-3">
            {s.points.map((p) => (
              <li key={p} className="flex items-center gap-3 text-white/80">
                <Check className="w-4 h-4 text-gold shrink-0" /> {p}
              </li>
            ))}
          </ul>
        </Reveal>
      </div>
    </div>
  );
}

export default function Services() {
  return (
    <div>
      <Seo
        title="Lighting & Electrical Supply, Installation & Sourcing in Toronto | Saver Plus Lighting Corp"
        description="Wholesale and retail lighting and electrical supply, licensed installation, and specialty product sourcing in Toronto & the GTA. Thousands of products from leading manufacturers — no minimum order."
        path="/services"
      />
      <section className="pt-40 md:pt-56 pb-12 max-w-[1400px] mx-auto px-5 md:px-10" data-testid="services-hero">
        <p className="text-xs uppercase tracking-[0.28em] text-gold/90 font-semibold mb-6 flex items-center gap-3">
          <span className="inline-block w-8 h-px bg-gold" /> Services
        </p>
        <h1 className="font-display uppercase font-medium text-[13vw] md:text-[8vw] leading-[0.85] tracking-tighter">
          <MaskedLines lines={["Supply.", "Install.", "Source."]} once={false} />
        </h1>
        <div className="mt-10 grid grid-cols-1 lg:grid-cols-12">
          <div className="lg:col-span-6 lg:col-start-7">
            <Reveal delay={0.2}>
              <p className="text-lg text-white/70 leading-relaxed">
                Everything you need to get the light right — competitive wholesale supply, licensed professional
                installation, and sourcing for thousands of products from leading manufacturers.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <Marquee text="Wholesale Pricing" separator="·" />

      <section className="max-w-[1400px] mx-auto px-5 md:px-10 py-20 md:py-28 space-y-24 md:space-y-40">
        {SERVICES.map((s, i) => (
          <ServiceRow key={s.num} s={s} index={i} />
        ))}
      </section>

      <section className="max-w-[1400px] mx-auto px-5 md:px-10 pb-24 md:pb-32">
        <div className="border border-white/10 bg-[#121212] p-10 md:p-16 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <h2 className="font-display uppercase text-3xl md:text-5xl tracking-tight leading-[0.95] max-w-xl">
            Need a quote? You'll hear back fast.
          </h2>
          <Link
            to="/contact"
            data-testid="services-quote-cta"
            className="inline-flex items-center gap-2 bg-gold text-white text-sm uppercase tracking-[0.14em] font-semibold px-7 py-4 hover:bg-[#b00000] transition-colors duration-300 shrink-0"
          >
            Request a Quote <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
